/**
 * Exposure Apparel — print placement catalogue + garment illustrations.
 *
 * Shared by the product page builder (custom-main-product.js) and the cart
 * "Add customisation" pop-up (ea-cart-customisations.js) so both offer the same
 * locations, sizes and drawings.
 *
 * Front view is drawn as you look at the wearer, so the wearer's LEFT chest,
 * side and sleeve appear on the viewer's RIGHT. Back view is not mirrored.
 */
(function () {
  'use strict';

  // Same catalogue as APPAREL_PRINT_LOCATIONS in custom-main-product.js
  const AREAS = [
    {
      id: 'Front',
      view: 'front',
      spots: [
        { value: 'Front Left Chest', label: 'Left Chest' },
        { value: 'Center Front', label: 'Centre Chest' },
        { value: 'Full Front', label: 'Full Front' },
        { value: 'Front Right Chest', label: 'Right Chest', advanced: true },
        { value: 'Over Pocket', label: 'Over Pocket', advanced: true },
        { value: 'Lower Front', label: 'Lower Front', advanced: true },
        { value: 'Lower Left', label: 'Lower Left', advanced: true },
        { value: 'Lower Right', label: 'Lower Right', advanced: true },
        { value: 'Vertical Left', label: 'Vertical Left', advanced: true },
        { value: 'Vertical Right', label: 'Vertical Right', advanced: true },
        { value: 'Left Leg', label: 'Left leg', legs: true },
        { value: 'Right Leg', label: 'Right leg', legs: true },
      ],
    },
    {
      id: 'Back',
      view: 'back',
      spots: [
        { value: 'Top Back', label: 'Upper Back' },
        { value: 'Center Back', label: 'Full Back' },
        { value: 'Lower Back', label: 'Lower Back', advanced: true },
        { value: 'Vertical Back L', label: 'Vertical Left', advanced: true },
        { value: 'Vertical Back R', label: 'Vertical Right', advanced: true },
      ],
    },
    {
      id: 'Sleeves',
      view: 'front',
      spots: [
        { value: 'Left Sleeve', label: 'Left Sleeve' },
        { value: 'Right Sleeve', label: 'Right Sleeve' },
      ],
    },
    {
      id: 'Sides',
      view: 'front',
      spots: [
        { value: 'Left Side', label: 'Left Side' },
        { value: 'Right Side', label: 'Right Side' },
      ],
    },
    // Hats (caps, bucket hats, beanies): embroidery only. One area, four spots; each spot carries the
    // drawing view it needs. Labels are re-worded per hat kind in areasFor (e.g. "Cuff front" on a beanie).
    {
      id: 'Embroidery placement',
      view: 'front',
      hat: true,
      spots: [
        { value: 'Hat Front', label: 'Hat front', view: 'front' },
        { value: 'Hat Left Side', label: 'Hat left side', view: 'side' },
        { value: 'Hat Right Side', label: 'Hat right side', view: 'side' },
        { value: 'Hat Back', label: 'Hat back', view: 'back' },
      ],
    },
  ];

  // ---------------------------------------------------------------------------
  // Hats. A handle is a hat when its name says so (custom-trucker-cap, custom-bucket-hat, custom-beanie...)
  // or when its calibration entry has kind cap / bucket / beanie.
  // ---------------------------------------------------------------------------
  const HAT_SPOT_VALUES = ['Hat Front', 'Hat Left Side', 'Hat Right Side', 'Hat Back'];
  const isHatSpotValue = (v) => HAT_SPOT_VALUES.includes(v);
  function hatKindOf(handle) {
    const h = String(handle || '').toLowerCase();
    const cal = window.EA_DESIGN_CAL && window.EA_DESIGN_CAL.products && window.EA_DESIGN_CAL.products[h];
    if (cal && ['cap', 'bucket', 'beanie'].includes(cal.kind)) return cal.kind;
    if (/beanie/.test(h)) return 'beanie';
    if (/bucket/.test(h)) return 'bucket';
    if (/(^|-)(caps?|trucker|visor|hat)(-|$)/.test(h)) return 'cap';
    return null;
  }
  const HAT_LABELS = {
    cap: { 'Hat Front': 'Front panel', 'Hat Left Side': 'Left side', 'Hat Right Side': 'Right side', 'Hat Back': 'Back (above strap)' },
    bucket: { 'Hat Front': 'Front of crown', 'Hat Left Side': 'Left side', 'Hat Right Side': 'Right side', 'Hat Back': 'Back of crown' },
    beanie: { 'Hat Front': 'Cuff front', 'Hat Left Side': 'Cuff left', 'Hat Right Side': 'Cuff right', 'Hat Back': 'Cuff back' },
  };
  const BRA_LABELS = { 'Front Left Chest': 'Left cup', 'Front Right Chest': 'Right cup', 'Lower Left': 'Lower left cup', 'Lower Right': 'Lower right cup' };
  // Names an older hat builder (and early orders) used for these positions
  const HAT_ALIASES = { 'Hat Center Front': 'Hat Front', 'Front Centre Logo': 'Hat Front', 'Hat Side Temple': 'Hat Left Side', 'Left Temple': 'Hat Left Side', 'Right Temple': 'Hat Right Side', 'Centre Back Arch': 'Hat Back' };

  // Print positions Tapstitch can actually print on each blank (from the blank's saved design in
  // the Tapstitch account, Oct 2026; the 24 Oct-4 imports were checked 2026-10-11). Sleeves are only offered where Tapstitch lists left/right
  // sleeve; no blank lists side-seam prints, so "Sides" is never offered. Products not listed here
  // get Front + Back only. Add new products here when they're imported (and to ea-design-calibration.js).
  const TAPSTITCH_AREAS = {
    'acid-wash-long-sleeve-tee': ["front", "back", "left_sleeve", "right_sleeve"],
    'boxy-fleece-crew': ["front", "back"],
    'boxy-fleece-hoodie': ["front", "back", "left_sleeve", "right_sleeve"],
    'boxy-heavyweight-tee': ["front", "back", "left_sleeve", "right_sleeve"],
    'boxy-quarter-zip-sweatshirt': ["front", "back"],
    'classic-cotton-crew-sweatshirt': ["front", "back"],
    'classic-cotton-hoodie-300gsm': ["front", "back"],
    'classic-cotton-tee-180gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'collared-soccer-jersey': ["front", "back"],
    'colour-block-track-jacket': ["front", "back"],
    'contrast-piping-bomber-jacket': ["front", "back"],
    'contrast-piping-polo-220gsm': ["front", "back"],
    'contrast-piping-raglan-hoodie': ["front", "back"],
    'contrast-trim-polo': ["front", "back"],
    'cool-touch-boxy-tee-220gsm': ["front", "back"],
    'cropped-yoga-tank': ["front", "back"],
    'drop-shoulder-crew-sweatshirt': ["front", "back"],
    'drop-shoulder-polo': ["front", "back"],
    'essential-boxy-tee': ["front", "back"],
    'essential-cotton-tee-220gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'essential-fleece-joggers-320gsm': ["front", "back"],
    'essential-fleece-mock-neck-crew-sweatshirt': ["front", "back", "left_sleeve", "right_sleeve"],
    'essential-long-sleeve-tee': ["front", "back"],
    'essential-oversized-hoodie': ["front", "back"],
    'essential-straight-leg-sweatpants': ["front", "back"],
    'everyday-cotton-tee-230gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'extended-size-cotton-tee-260gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'extra-heavyweight-oversized-hoodie': ["front", "back"],
    'full-zip-fleece-hoodie-350gsm': ["front", "back"],
    'half-zip-cotton-polo': ["front", "back"],
    'halter-sports-bra': ["front"],
    'heavyweight-cotton-tee-260gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-crew-sweatshirt-320gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-drop-shoulder-tee-300gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-earth-tone-tee-305gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-fleece-shorts-420gsm': ["front", "back"],
    'heavyweight-premium-hoodie-430gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-pullover-hoodie': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-quarter-zip-sweatshirt': ["front", "back", "left_sleeve", "right_sleeve"],
    'heavyweight-sweatpants': ["front", "back"],
    'heavyweight-zip-hoodie': ["front", "back"],
    'high-waisted-bike-shorts': ["front", "back"],
    'high-waisted-leggings': ["front", "back"],
    'kids-coach-jacket': ["front", "back"],
    'kids-cotton-tee-180gsm': ["front", "back"],
    'kids-fleece-crew': ["front", "back"],
    'kids-fleece-hoodie': ["front", "back"],
    'kids-long-sleeve-tee': ["front", "back"],
    'kids-quick-dry-sports-tee': ["front", "back"],
    'kids-sports-shorts': ["front", "back"],
    'kids-sweatpants': ["front", "back"],
    'lightweight-hooded-training-jacket': ["front", "back"],
    'lightweight-sports-singlet': ["front", "back"],
    'long-sleeve-training-top': ["front", "back"],
    'loose-fit-singlet': ["front", "back"],
    'mens-cotton-tank-top': ["front", "back"],
    'mens-lined-running-shorts': ["front", "back"],
    'mens-mock-neck-training-tee': ["front", "back"],
    'mens-performance-running-tee': ["front", "back"],
    'mens-tapered-training-pants': ["front", "back"],
    'mesh-basketball-shorts': ["front", "back"],
    'mesh-training-shorts': ["front", "back"],
    'mineral-wash-boxy-tee-275gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'oversized-crew-sweatshirt-290gsm': ["front", "back"],
    'oversized-drop-shoulder-tee': ["front", "back", "left_sleeve", "right_sleeve"],
    'oversized-fleece-hoodie-350gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'oversized-long-sleeve-tee-305gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'performance-quarter-zip': ["front", "back"],
    'performance-sports-tee-150gsm': ["front", "back"],
    'premium-heavyweight-crew-420gsm': ["front", "back"],
    'quarter-zip-polo': ["front", "back"],
    'quarter-zip-training-top': ["front", "back"],
    'quick-dry-cooling-singlet': ["front", "back"],
    'quick-dry-cooling-tee': ["front", "back"],
    'quick-dry-relaxed-tee-200gsm': ["front", "back"],
    'reflective-raglan-running-tee': ["front", "back"],
    'ringer-tee': ["front", "back"],
    'seamless-cotton-tee-200gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'side-stripe-fleece-track-jacket': ["front", "back"],
    'side-stripe-track-pants': ["front", "back"],
    'slim-fit-stretch-tee': ["front", "back"],
    'snow-wash-heavyweight-hoodie': ["front", "back", "left_sleeve", "right_sleeve"],
    'snow-wash-oversized-crew': ["front", "back", "left_sleeve", "right_sleeve"],
    'snow-wash-raw-hem-sweatpants': ["front", "back"],
    'snow-washed-oversized-cotton-t-shirt': ["front", "back", "left_sleeve", "right_sleeve"],
    'stand-collar-bomber-jacket': ["front", "back"],
    'stand-collar-track-jacket': ["front", "back"],
    'straight-leg-fleece-track-pants': ["front", "back"],
    'stretch-crew-tee-180gsm': ["front", "back", "left_sleeve", "right_sleeve"],
    'stretch-raglan-polo': ["front", "back"],
    'striped-football-jersey': ["front", "back"],
    'structured-heavyweight-crew': ["front", "back"],
    'structured-heavyweight-hoodie': ["front", "back"],
    'structured-heavyweight-joggers': ["front", "back"],
    'sunfade-crew': ["front", "back"],
    'sunfade-hoodie': ["front", "back"],
    'sunfade-long-sleeve-tee': ["front", "back", "left_sleeve", "right_sleeve"],
    'sunfade-sweatpants': ["front", "back"],
    'two-tone-fleece-varsity-jacket': ["front", "back"],
    'ultra-heavyweight-cotton-crew': ["front", "back"],
    'ultra-heavyweight-cotton-hoodie': ["front", "back"],
    'ultra-heavyweight-cotton-sweatpants': ["front", "back"],
    'ultra-heavyweight-tee': ["front", "back"],
    'varsity-baseball-jacket': ["front", "back"],
    'vintage-wash-barrel-leg-sweatpants': ["front", "back"],
    'vintage-wash-boxy-hoodie': ["front", "back"],
    'vintage-wash-crew-sweatshirt': ["front", "back"],
    'vintage-wash-oversized-tee': ["front", "back"],
    'vintage-washed-long-sleeve-tee': ["front", "back", "left_sleeve", "right_sleeve"],
    'washed-heavyweight-singlet': ["front", "back"],
    'womens-cropped-hoodie': ["front", "back", "left_sleeve", "right_sleeve"],
    'womens-cropped-quarter-zip': ["front", "back"],
    'womens-cropped-tank': ["front", "back"],
    'womens-cropped-zip-hoodie': ["front", "back"],
    'womens-fitted-crop-polo': ["front", "back"],
    'womens-fitted-crop-tee': ["front", "back"],
    'womens-fitted-training-jacket': ["front", "back"],
    'womens-fitted-training-tee': ["front", "back"],
    'womens-slim-fit-tee': ["front", "back"],
  };

  function allowedAreaIds(handle) {
    if (hatKindOf(handle)) return ['Embroidery placement'];
    const a = TAPSTITCH_AREAS[handle] || ['front', 'back'];
    const ids = [];
    if (a.includes('front')) ids.push('Front');
    if (a.includes('back')) ids.push('Back');
    if (a.includes('left_sleeve') || a.includes('right_sleeve')) ids.push('Sleeves');
    if (a.includes('left_side') || a.includes('right_side')) ids.push('Sides');
    return ids;
  }

  // What Tapstitch's printable areas actually allow on each kind of garment (from the
  // per-product calibration in ea-design-calibration.js, measured off their saved designs):
  //   top (tee, crew, long sleeve, singlet): full front and full back (Tapstitch's real size, roughly 33–40 × 42–51 cm) → every spot
  //   hoodie (pullover): the front area stops ABOVE the kangaroo pocket (19–32 cm high), so no
  //       lower-front, over-pocket or vertical prints; back is full
  //   zip hoodie, jacket, quarter/half zip: front = left chest + right chest only; back is full
  //   polo: left/right chest + a lower front panel under the placket; no full front
  //   pants / shorts: one print per leg (roughly 12–20 cm wide), nothing else
  //   bra (sports bra): Tapstitch's four small cup panels only, no back
  // Products without calibration fall back to the keyword rule, with front + back only.
  const SPOTS_BY_KIND = {
    top: null, // every spot
    hoodie: ['Front Left Chest', 'Center Front', 'Full Front', 'Front Right Chest', 'Top Back', 'Center Back', 'Lower Back', 'Vertical Back L', 'Vertical Back R'],
    chest: ['Front Left Chest', 'Front Right Chest', 'Top Back', 'Center Back', 'Lower Back', 'Vertical Back L', 'Vertical Back R'],
    polo: ['Front Left Chest', 'Front Right Chest', 'Lower Front', 'Top Back', 'Center Back', 'Lower Back'],
    pants: ['Left Leg', 'Right Leg'],
    bra: ['Front Left Chest', 'Front Right Chest', 'Lower Left', 'Lower Right'],
    cap: HAT_SPOT_VALUES,
    bucket: HAT_SPOT_VALUES,
    beanie: HAT_SPOT_VALUES,
  };
  const LEG_SPOTS = ['Left Leg', 'Right Leg'];

  function kindOf(handle) {
    const h = String(handle || '').toLowerCase();
    const hatKind = hatKindOf(h);
    if (hatKind) return hatKind;
    const cal = window.EA_DESIGN_CAL && window.EA_DESIGN_CAL.products && window.EA_DESIGN_CAL.products[h];
    if (cal) {
      const front = (cal.views && cal.views.front) || [];
      const ids = front.map((a) => a.id);
      if (cal.kind === 'pants' || ids.includes('leg-left')) return 'pants';
      if (cal.kind === 'bra') return 'bra';
      if (ids.length && !ids.includes('front')) return 'chest'; // zip hoodies: chest panels only
      if (/polo/.test(h)) return 'polo';
      if (cal.kind === 'hoodie') return 'hoodie';
      return 'top';
    }
    if (/sweatpant|jogger|short|pants/.test(h)) return 'pants';
    if (/polo/.test(h)) return 'polo';
    if (/zip|jacket|bomber|coach/.test(h)) return 'chest';
    if (/hoodie/.test(h)) return 'hoodie';
    return 'top';
  }

  function allowedSpotValues(handle) {
    const kind = kindOf(handle);
    const list = SPOTS_BY_KIND[kind];
    if (hatKindOf(handle)) return HAT_SPOT_VALUES.slice();
    if (!list) return AREAS.flatMap((a) => a.spots.filter((sp) => !sp.legs).map((sp) => sp.value));
    // Sleeve and side spots stay gated by the per-product area map (allowedAreaIds), not by kind.
    return kind === 'pants' || kind === 'bra' ? list.slice() : list.concat(['Left Sleeve', 'Right Sleeve', 'Left Side', 'Right Side']);
  }

  function areasFor(handle) {
    const ids = allowedAreaIds(handle);
    const kind = kindOf(handle);
    const allowed = allowedSpotValues(handle);
    return AREAS
      .filter((area) => ids.includes(area.id) && (kind !== 'pants' || area.id === 'Front'))
      .map((area) => ({
        ...area,
        spots: area.spots
          .filter((sp) => allowed.includes(sp.value) && fitsSafeArea(handle, sp.value))
          .map((sp) => (area.hat ? { ...sp, label: (HAT_LABELS[kind] || {})[sp.value] || sp.label }
            : kind === 'bra' ? { ...sp, label: BRA_LABELS[sp.value] || sp.label } : sp)),
      }))
      .filter((area) => area.spots.length);
  }

  // Same rule as getLocationSize() in custom-main-product.js
  const LARGE = [
    'Full Front', 'Lower Front', 'Vertical Left', 'Vertical Right',
    'Top Back', 'Center Back', 'Lower Back', 'Vertical Back L', 'Vertical Back R',
  ];

  const spotIndex = {};
  AREAS.forEach((area) => area.spots.forEach((spot) => { spotIndex[spot.value] = { ...spot, area: area.id, view: spot.view || area.view }; }));

  function getSize(value, method, handle) {
    if (window.EA_PRINT_RULES) return window.EA_PRINT_RULES.locationSize(value, method, handle);
    return LARGE.includes(value) ? 'Large' : 'Small';
  }

  function getSpot(value) {
    return spotIndex[value] || null;
  }

  // Placement areas in garment units (viewBox 0 0 120 150; body x 31–89, collar y≈20, hem y≈128)
  const centred = (offset, w) => 60 + offset - w / 2;
  const RECTS = {
    'Front Left Chest': { x: centred(11, 12), y: 35, width: 12, height: 10 },
    'Front Right Chest': { x: centred(-11, 12), y: 35, width: 12, height: 10 },
    'Center Front': { x: centred(0, 14), y: 33, width: 14, height: 10 },
    'Full Front': { x: centred(0, 34), y: 34, width: 34, height: 44 },
    'Over Pocket': { x: centred(0, 28), y: 96, width: 28, height: 16 },
    'Lower Front': { x: centred(0, 32), y: 104, width: 32, height: 16 },
    'Lower Left': { x: centred(16, 16), y: 104, width: 16, height: 16 },
    'Lower Right': { x: centred(-16, 16), y: 104, width: 16, height: 16 },
    'Vertical Left': { x: centred(18, 8), y: 36, width: 8, height: 76 },
    'Vertical Right': { x: centred(-18, 8), y: 36, width: 8, height: 76 },
    'Top Back': { x: centred(0, 36), y: 30, width: 36, height: 12 },
    'Center Back': { x: centred(0, 36), y: 33, width: 36, height: 50 },
    'Lower Back': { x: centred(0, 32), y: 104, width: 32, height: 16 },
    'Vertical Back L': { x: centred(-18, 8), y: 34, width: 8, height: 78 },
    'Vertical Back R': { x: centred(18, 8), y: 34, width: 8, height: 78 },
    // Sleeves follow the sleeve's 45° axis
    'Left Sleeve': { x: 95, y: 36.5, width: 9, height: 12, rot: -45 },
    'Right Sleeve': { x: 16, y: 36.5, width: 9, height: 12, rot: 45 },
    // Side seams: narrow strip from under the arm to the hem
    'Left Side': { x: 84, y: 54, width: 4.5, height: 66 },
    'Right Side': { x: 31.5, y: 54, width: 4.5, height: 66 },
    // Pants: one print per thigh (wearer's left appears on the viewer's right)
    'Left Leg': { x: 66, y: 48, width: 12, height: 30 },
    'Right Leg': { x: 42, y: 48, width: 12, height: 30 },
  };

  // Which drawing a product gets, from its handle. Order matters: "stretch-crew-tee" is a
  // tee, "quarter-zip-polo" a polo, "long-sleeve-tee" a long sleeve (crew shape).
  //   tee      – T-shirts, activewear tees, jerseys
  //   polo     – polos (collar + button placket)
  //   tank     – singlets and tanks (no sleeves)
  //   bra      – sports bras (halter)
  //   hoodie   – pullover + zip hoodies, hooded jackets (zip hoodies also get a zip line)
  //   crew     – crewneck sweatshirts, long sleeves, quarter-zips, jackets (long sleeves, rib cuffs + hem)
  //   pants / shorts / leggings – bottoms (one print per leg)
  function garmentOf(handle) {
    const h = String(handle || '').toLowerCase();
    const hatKind = hatKindOf(h);
    if (hatKind) return hatKind;
    if (/legging/.test(h)) return 'leggings';
    if (/short/.test(h)) return 'shorts';
    if (/pant|jogger|trouser/.test(h)) return 'pants';
    if (/(^|-)bra(-|$)/.test(h)) return 'bra';
    if (/long-sleeve/.test(h)) return 'crew';
    if (/hoodie|hooded/.test(h)) return 'hoodie';
    if (/polo/.test(h)) return 'polo';
    if (/singlet|tank/.test(h)) return 'tank';
    if (/tee/.test(h)) return 'tee';
    if (/crew|sweatshirt|quarter-zip|jacket|bomber|coach/.test(h)) return 'crew';
    return 'tee';
  }
  const hasZip = (handle) => /zip|jacket|bomber|coach/.test(String(handle || '').toLowerCase()) && !/polo/.test(String(handle || '').toLowerCase());
  const pageHandle = () => decodeURIComponent((location.pathname.match(/\/products\/([^/?#]+)/) || [])[1] || '');

  // Long sleeves run down to the hip, so a sleeve print sits on the upper arm at ~12°
  const LONG_SLEEVES = {
    'Left Sleeve': { x: 91.7, y: 50, width: 7, height: 14, rot: -12.4 },
    'Right Sleeve': { x: 21.3, y: 50, width: 7, height: 14, rot: 12.4 },
  };
  // Per-garment print areas where they differ from the tee: hoodie chest prints sit below
  // the hood and drawstrings and the full front stops above the pocket; on the back the
  // hood covers the top, so back prints start lower.
  const RECTS_BY_GARMENT = {
    hoodie: {
      ...LONG_SLEEVES,
      'Front Left Chest': { x: centred(13.5, 12), y: 40, width: 12, height: 10 },
      'Front Right Chest': { x: centred(-13.5, 12), y: 40, width: 12, height: 10 },
      'Center Front': { x: centred(0, 14), y: 47, width: 14, height: 10 },
      'Full Front': { x: centred(0, 34), y: 46, width: 34, height: 40 },
      'Top Back': { x: centred(0, 36), y: 48, width: 36, height: 12 },
      'Center Back': { x: centred(0, 36), y: 48, width: 36, height: 48 },
      'Vertical Back L': { x: centred(-18, 8), y: 48, width: 8, height: 66 },
      'Vertical Back R': { x: centred(18, 8), y: 48, width: 8, height: 66 },
    },
    crew: { ...LONG_SLEEVES },
    // Bottoms: a print on each thigh, inside that leg of the drawing
    shorts: {
      'Left Leg': { x: 65, y: 56, width: 14, height: 22 },
      'Right Leg': { x: 41, y: 56, width: 14, height: 22 },
    },
    leggings: {
      'Left Leg': { x: 63.5, y: 50, width: 10, height: 26 },
      'Right Leg': { x: 46.5, y: 50, width: 10, height: 26 },
    },
  };
  // Embroidery boxes on the hat drawings (same 120 x 150 units). Right Side is the mirror of Left Side.
  const HAT_RECTS = {
    cap: {
      'Hat Front': { x: 45, y: 46, width: 30, height: 18 },
      'Hat Left Side': { x: 55, y: 52, width: 19, height: 10 },
      'Hat Right Side': { x: 46, y: 52, width: 19, height: 10 },
      'Hat Back': { x: 47, y: 60, width: 26, height: 10 },
    },
    bucket: {
      'Hat Front': { x: 45, y: 44, width: 30, height: 21 },
      'Hat Left Side': { x: 48, y: 50, width: 22, height: 14 },
      'Hat Right Side': { x: 50, y: 50, width: 22, height: 14 },
      'Hat Back': { x: 46, y: 46, width: 28, height: 16 },
    },
    beanie: {
      'Hat Front': { x: 44, y: 84, width: 32, height: 16 },
      'Hat Left Side': { x: 48, y: 86, width: 24, height: 12 },
      'Hat Right Side': { x: 48, y: 86, width: 24, height: 12 },
      'Hat Back': { x: 44, y: 84, width: 32, height: 16 },
    },
  };
  const rectFor = (location, garment) => (HAT_RECTS[garment] ? HAT_RECTS[garment][location] : (RECTS_BY_GARMENT[garment] || {})[location] || RECTS[location]);

  // ---------------------------------------------------------------------------
  // Tapstitch safe areas. Every spot has to fit inside the blank's printable area (the
  // dashed box in Tapstitch's editor), measured per product in ea-design-calibration.js:
  // zone x/y are photo fractions, wCm/hCm the area in cm, hps = collar line, cx = centre,
  // s = photo fraction per cm. How a spot's default size (EA_PRINT_RULES) is fitted in:
  //   fill  (full front/back, legs)  shrinks to the area: a 19 cm tall hoodie front gets 28 × 19
  //   band  (upper/lower bands)      keeps its height, may narrow to 60% of its width
  //   long  (verticals)              the full length must be available
  //   logo  (chest, sleeves, ...)    must fit at 75%+ of its size
  // A spot that doesn't fit isn't offered. Drawings place each spot to scale inside its area.
  // ---------------------------------------------------------------------------
  const FIT = {
    'Full Front': 'fill', 'Center Back': 'fill', 'Left Leg': 'fill', 'Right Leg': 'fill',
    'Top Back': 'band', 'Lower Back': 'band', 'Lower Front': 'band', 'Over Pocket': 'band',
    'Vertical Left': 'long', 'Vertical Right': 'long', 'Vertical Back L': 'long', 'Vertical Back R': 'long',
  };
  const MIN_RATIO = { fill: [0, 0], band: [0.6, 0.9], long: [0.8, 0.97], logo: [0.75, 0.75] };
  // Where a spot sits in its area. Sides are the WEARER's (front view is mirrored).
  const PLACE = {
    'Full Front': 'top', 'Center Back': 'top', 'Top Back': 'top',
    'Center Front': 'chest', 'Front Left Chest': 'chest-left', 'Front Right Chest': 'chest-right',
    'Lower Front': 'bottom', 'Lower Back': 'bottom', 'Over Pocket': 'bottom',
    'Lower Left': 'bottom-left', 'Lower Right': 'bottom-right',
    'Vertical Left': 'side-left', 'Vertical Right': 'side-right', 'Vertical Back L': 'side-left', 'Vertical Back R': 'side-right',
  };
  const CHEST_DROP_CM = 2; // chest prints sit just under the top of the area
  const CHEST_OFFSET_CM = 9; // max distance of a chest logo's centre from the centre line
  const CM = 1.2; // drawing units per cm (the drawings are stylised, so one uniform scale)
  const CM_BY_GARMENT = { bra: 2 }; // a sports bra is small, so its drawing is shown larger

  function calFor(handle) {
    const all = window.EA_DESIGN_CAL;
    if (!all) return null;
    const h = String(handle || '').toLowerCase();
    if (all.products && all.products[h]) return all.products[h];
    const kind = kindOf(h);
    return (all.defaults || {})[kind === 'hoodie' ? 'hoodie' : kind === 'pants' ? 'pants' : 'top'] || null;
  }

  function zoneFor(cal, location) {
    const spot = getSpot(location);
    if (!cal || !spot || !cal.views) return null;
    const ids = {
      'Front Left Chest': ['front-left', 'front'],
      'Front Right Chest': ['front-right', 'front'],
      'Left Leg': ['leg-left'],
      'Right Leg': ['leg-right'],
      'Lower Left': ['front-lower-left', 'front'],
      'Lower Right': ['front-lower-right', 'front'],
    }[location] || [spot.view === 'back' ? 'back' : 'front'];
    const zones = cal.views[spot.view === 'back' ? 'back' : 'front'] || [];
    for (const id of ids) {
      const z = zones.find((zone) => zone.id === id);
      if (z) return z;
    }
    return null;
  }

  // The spot's printed box in cm: x from the centre line (viewer's left = negative), y below the collar
  function printBox(handle, location) {
    if (isHatSpotValue(location)) return null; // hats have no photo-calibrated safe areas; the drawings place each spot
    const d = window.EA_PRINT_RULES && window.EA_PRINT_RULES.LOCATION_DEFAULTS && window.EA_PRINT_RULES.LOCATION_DEFAULTS[location];
    const spot = getSpot(location);
    if (!d || !spot) return null;
    const cal = calFor(handle);
    if (/Sleeve$/.test(location)) {
      if (!cal || !cal.sleeve) return null;
      const w = Math.min(d.w, cal.sleeve.wCm);
      const h = Math.min(d.h, cal.sleeve.hCm);
      return w >= d.w * MIN_RATIO.logo[0] && h >= d.h * MIN_RATIO.logo[1] ? { w, h, sleeve: true } : null;
    }
    const zone = zoneFor(cal, location);
    if (!zone) return null;
    const W = zone.wCm;
    const H = zone.hCm;
    const fit = FIT[location] || 'logo';
    const w = Math.min(d.w, W);
    const h = Math.min(d.h, H);
    // Blanks whose printable panels are smaller than a standard logo (sports bra cups): the logo shrinks to the panel
    const [minW, minH] = cal.smallZones ? [0, 0] : MIN_RATIO[fit];
    if (w + 0.01 < d.w * minW || h + 0.01 < d.h * minH) return null;

    const isChestZone = /^front-(lower-)?(left|right)$/.test(zone.id);
    const back = spot.view === 'back';
    const place = PLACE[location] || 'top';
    const side = /left/.test(place) ? 1 : /right/.test(place) ? -1 : 0; // wearer's left = +1
    const sign = back ? -side : side; // front view is mirrored, so wearer's left is the viewer's right
    let dx = 0; // centre offset inside the area, viewer frame
    let y = 0;
    if (isChestZone || place === 'top') {
      y = 0;
    } else if (place.startsWith('chest')) {
      y = Math.min(CHEST_DROP_CM, H - h);
      dx = sign * Math.min(CHEST_OFFSET_CM, W / 2 - w / 2);
    } else if (place.startsWith('bottom')) {
      y = H - h;
      dx = sign * (W / 2 - w / 2);
    } else if (place.startsWith('side')) {
      y = 0;
      dx = sign * (W / 2 - w / 2);
    }
    const areaLeft = (zone.x - cal.cx) / cal.s;
    const areaTop = (zone.y - cal.hps) / cal.s;
    return {
      x: areaLeft + W / 2 + dx - w / 2,
      y: areaTop + y,
      w,
      h,
      area: { x: areaLeft, y: areaTop, w: W, h: H },
    };
  }

  // The print box on a blank flagged smallZones (sports bra cups), so it's priced on its real size; else null
  function smallZoneBox(handle, location) {
    const cal = calFor(handle);
    return cal && cal.smallZones ? printBox(handle, location) : null;
  }

  // Spots with no calibrated area (sides, hats...) fall back to the per-kind rules alone
  function fitsSafeArea(handle, location) {
    if (isHatSpotValue(location)) return true;
    const known = (window.EA_PRINT_RULES && window.EA_PRINT_RULES.LOCATION_DEFAULTS || {})[location];
    if (!known || !calFor(handle) || /Side$/.test(location)) return true;
    return !!printBox(handle, location);
  }

  // Drawing units for a spot (and its safe area) on this product, or null for the static rect
  const COLLAR_Y = { tee: 20, polo: 20, tank: 20, bra: 20, crew: 20, hoodie: 22 };
  function drawnBox(handle, location, garment) {
    const box = printBox(handle, location);
    if (!box || box.sleeve || LEG_SPOTS.includes(location)) return null;
    const top = COLLAR_Y[garment] || 20;
    const k = CM_BY_GARMENT[garment] || CM;
    const toRect = (b) => ({ x: 60 + b.x * k, y: top + b.y * k, width: b.w * k, height: b.h * k });
    return { rect: toRect(box), area: toRect(box.area) };
  }

  // Brand palette (badge logo): slate ink, white garment, mustard print area with an orange edge
  // (teal at low strength read as the old cyan)
  const INK = '#414a59';
  const FABRIC = '#ffffff';
  const ACCENT = '#c77d22';
  const ACCENT_FILL = '#edbe4f';
  const outline = (d) => `<path d="${d}" fill="${FABRIC}" stroke="${INK}" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"/>`;
  const line = (d, width = 1.1) => `<path d="${d}" fill="none" stroke="${INK}" stroke-width="${width}" stroke-linecap="round"/>`;
  const stitch = (d) => `<path d="${d}" fill="none" stroke="${INK}" stroke-width="0.8" stroke-dasharray="1.6 1.2" opacity="0.55"/>`;

  // Long-sleeved body shared by the crew and the hoodie (sleeves to the hip, rib cuffs + hem band)
  const LONG_BODY = 'L 93 26 L 113 110 L 101 113 L 89 52 L 89 128 Q 60 130.5 31 128 L 31 52 L 19 113 L 7 110 L 27 26 Z';
  const LONG_RIBS = line('M 111.6 104.2 L 99.8 107.1 M 8.4 104.2 L 20.2 107.1 M 31 121 Q 60 123.5 89 121', 0.9) +
    stitch('M 92 28 L 89 52 M 28 28 L 31 52');

  function drawGarment(garment, isBack, zip) {
    if (garment === 'hoodie') {
      if (isBack) {
        // Hood lying flat over the upper back
        return outline('M 42 22 ' + LONG_BODY.replace(/^L 93 26/, 'L 78 22 L 93 26')) + LONG_RIBS +
          outline('M 40 24 Q 36 6 60 4 Q 84 6 80 24 Q 77 33 60 36 Q 43 33 40 24 Z') +
          stitch('M 60 6 L 60 34');
      }
      return outline('M 42 22 ' + LONG_BODY.replace(/^L 93 26/, 'L 78 22 L 93 26')) + LONG_RIBS +
        // hood around the face opening, meeting in a V at the neck
        outline('M 42 24 Q 38 8 60 5 Q 82 8 78 24 Q 70 32 60 34 Q 50 32 42 24 Z') +
        `<path d="M 47 23 Q 47 12 60 11 Q 73 12 73 23 Q 67 29 60 30 Q 53 29 47 23 Z" fill="${INK}" fill-opacity="0.07" stroke="${INK}" stroke-width="1"/>` +
        (zip
          ? line('M 60 34 L 60 128', 1) + line('M 58.6 40 L 61.4 40 L 61.4 45 L 58.6 45 Z', 0.8)
          : line('M 56 32 L 55.6 41.5 M 64 32 L 64.4 41.5', 0.9) + line('M 55.6 41.5 L 55.5 44.5 M 64.4 41.5 L 64.5 44.5', 1.8) +
            // kangaroo pocket
            line('M 40 90 Q 60 88.5 80 90 L 85 115 Q 60 117 35 115 Z', 0.9));
    }
    if (garment === 'crew') {
      const neck = isBack ? 'Q 60 25 74 20' : 'Q 60 33 74 20';
      const rib = isBack ? 'M 43 21 Q 60 29 77 21 M 44.5 23.5 Q 60 31 75.5 23.5' : 'M 43 21 Q 60 38 77 21 M 44.5 23.5 Q 60 41 75.5 23.5';
      return outline(`M 46 20 ${neck} ${LONG_BODY}`) + line(rib, 0.9) + LONG_RIBS +
        (zip && !isBack ? line('M 60 26 L 60 128', 1) + line('M 58.6 31 L 61.4 31 L 61.4 36 L 58.6 36 Z', 0.8) : '');
    }
    if (garment === 'tank') {
      // Singlet: wide armholes, bound neck and arms
      const neck = isBack ? 'Q 60 27 72 18' : 'Q 60 38 72 18';
      return outline(`M 48 18 ${neck} L 80 18 Q 81 40 91 50 L 90 128 Q 60 130.5 30 128 L 29 50 Q 39 40 40 18 Z`) +
        line(isBack ? 'M 47 20.5 Q 60 30 73 20.5' : 'M 47 20.5 Q 60 41 73 20.5', 0.9) +
        stitch('M 78 20 Q 79.5 40 88.5 49 M 42 20 Q 40.5 40 31.5 49') +
        stitch('M 31.5 124.5 Q 60 127 88.5 124.5');
    }
    if (garment === 'bra') {
      // Halter sports bra, drawn large: strap round the neck, crossed cups, elastic underband
      return outline('M 46 12 Q 60 6 74 12 L 82 38 Q 99 58 96 86 L 95 106 Q 60 111 25 106 L 24 86 Q 21 58 38 38 Z') +
        `<path d="M 52 14 Q 60 11 68 14 L 65 40 Q 61 64 60 80 Q 59 64 55 40 Z" fill="${INK}" fill-opacity="0.07" stroke="${INK}" stroke-width="1"/>` +
        line('M 29 91 Q 47 80 60 80 Q 73 80 91 91', 0.9) +
        line('M 25 96 Q 60 101 95 96', 0.9) +
        stitch('M 26 102 Q 60 107 94 102');
    }
    if (garment === 'shorts') {
      return outline('M 33 38 L 87 38 L 93 98 Q 79 101 65 99 L 60 72 L 55 99 Q 41 101 27 98 Z') +
        line('M 33.4 45 L 86.6 45', 0.9) + line('M 57 45 Q 56 51 54 53 M 63 45 Q 64 51 66 53', 0.8) +
        stitch('M 28.5 93 Q 41 95.5 54.5 94 M 65.5 94 Q 79 95.5 91.5 93');
    }
    if (garment === 'leggings') {
      return outline('M 38 20 L 82 20 L 84 34 L 77 141 L 66 141 L 61 64 L 59 64 L 54 141 L 43 141 L 36 34 Z') +
        line('M 37.5 30 L 82.5 30', 0.9) +
        stitch('M 60 32 L 60 63 M 43.5 136 L 54.4 136 M 65.6 136 L 76.6 136');
    }
    if (garment === 'pants') {
      return outline('M 34 22 L 86 22 L 90 34 L 84 130 L 64 130 L 60 62 L 56 130 L 36 130 L 30 34 Z') +
        line('M 34 30 L 86 30') +
        stitch('M 38 126 L 56 126 M 64 126 L 82 126');
    }
    // tee (and polo: same body with a collar and button placket)
    const neckline = isBack ? 'Q 60 25 74 20' : 'Q 60 33 74 20';
    const ribLine = isBack ? 'M 43 21 Q 60 29 77 21' : 'M 43 21 Q 60 38 77 21';
    return outline(`M 46 20 ${neckline} L 93 26 L 111 48 L 101 57 L 89 48 L 89 128 Q 60 130.5 31 128 L 31 48 L 19 57 L 9 48 L 27 26 Z`) +
      line(ribLine) +
      stitch('M 11 45.8 L 21 54.8 M 109 45.8 L 99 54.8') +
      stitch('M 32 124.5 Q 60 127 88 124.5') +
      `<path d="M 31 48 L 31 52 M 89 48 L 89 52" fill="none" stroke="${INK}" stroke-width="0.8" opacity="0.4"/>` +
      (garment === 'polo'
        ? (isBack
          ? outline('M 45 20 Q 60 15 75 20 L 74 25 Q 60 21 46 25 Z')
          : outline('M 57 24 L 63 24 L 63 42 L 57 42 Z') +
            `<circle cx="60" cy="29" r="0.9" fill="${INK}"/><circle cx="60" cy="36" r="0.9" fill="${INK}"/>` +
            outline('M 46 19 L 60 25 L 52 33 L 43 23 Z') + outline('M 74 19 L 60 25 L 68 33 L 77 23 Z'))
        : '');
  }


  // ---------------------------------------------------------------------------
  // Hat drawings: cap, bucket hat and beanie, each as seen from the front, the wearer's left side and
  // the back. Same slate-and-white style as the garments; the embroidery box is drawn on top.
  // ---------------------------------------------------------------------------
  const HAT_DRAW = {
    cap: {
      front: () =>
        outline('M 31 84 C 29 48 43 31 60 31 C 77 31 91 48 89 84 Z') +
        line('M 60 32 L 60 82', 0.9) + line('M 47 37 C 43 52 42 68 43 82 M 73 37 C 77 52 78 68 77 82', 0.9) +
        `<circle cx="60" cy="31.5" r="2.2" fill="${FABRIC}" stroke="${INK}" stroke-width="1.2"/>` +
        outline('M 28 84 C 44 79 76 79 92 84 C 99 98 80 112 60 112 C 40 112 21 98 28 84 Z') +
        stitch('M 33 90 C 46 95 74 95 87 90 M 38 97 C 50 102 70 102 82 97'),
      side: () =>
        outline('M 33 90 C 28 58 44 32 68 32 C 92 32 104 58 100 90 Z') +
        line('M 68 33 C 62 52 60 72 62 90 M 47 40 C 44 58 44 76 46 90', 0.9) +
        `<circle cx="68" cy="32.5" r="2" fill="${FABRIC}" stroke="${INK}" stroke-width="1.1"/>` +
        outline('M 34 86 C 22 88 12 92 6 97 C 4 99 6 101 9 100 C 18 97 30 96 38 96 Z') +
        stitch('M 36 88 C 55 92 85 92 100 87'),
      back: () =>
        outline('M 30 84 C 29 48 43 31 60 31 C 77 31 91 48 90 84 Z') +
        line('M 60 32 L 60 82', 0.9) + line('M 47 37 C 43 52 42 68 43 82 M 73 37 C 77 52 78 68 77 82', 0.9) +
        `<circle cx="60" cy="31.5" r="2.2" fill="${FABRIC}" stroke="${INK}" stroke-width="1.2"/>` +
        outline('M 30 84 L 90 84 L 89 98 Q 60 104 31 98 Z') +
        line('M 46 91 L 74 91', 1.4) +
        `<rect x="55" y="87" width="10" height="7" rx="1.2" fill="${FABRIC}" stroke="${INK}" stroke-width="0.9"/>`,
    },
    bucket: {
      front: () =>
        outline('M 38 80 C 38 50 44 34 60 34 C 76 34 82 50 82 80 Z') +
        stitch('M 38 70 L 82 70 M 39 77 C 50 81 70 81 81 77') +
        outline('M 12 88 C 30 74 90 74 108 88 C 108 104 82 112 60 112 C 38 112 12 104 12 88 Z') +
        stitch('M 22 90 C 40 97 80 97 98 90'),
      side: () =>
        outline('M 34 80 C 34 52 44 33 64 33 C 82 33 90 52 90 80 Z') +
        stitch('M 35 68 L 89 68 M 34 75 C 50 79 74 79 89 75') +
        outline('M 6 86 C 30 76 90 76 114 86 C 96 100 24 100 6 86 Z') +
        stitch('M 16 88 C 40 94 80 94 104 88'),
      back: () => HAT_DRAW.bucket.front(),
    },
    beanie: {
      front: () =>
        outline('M 34 96 C 32 56 44 33 60 33 C 76 33 88 56 86 96 Z') +
        stitch('M 44 40 C 42 56 41 70 41 80 M 60 34 L 60 78 M 76 40 C 78 56 79 70 79 80') +
        outline('M 31 80 Q 60 76 89 80 L 91 106 Q 60 110 29 106 Z') +
        line('M 40 80.8 L 39 107 M 50 79.2 L 50 108.4 M 60 78.8 L 60 109 M 70 79.2 L 70 108.4 M 80 80.8 L 81 107', 0.8),
      side: () =>
        outline('M 32 96 C 30 56 44 32 64 33 C 84 34 92 58 90 96 Z') +
        stitch('M 46 40 C 44 56 44 70 44 80 M 66 34 C 64 50 64 66 64 80') +
        outline('M 30 82 Q 62 76 92 82 L 94 108 Q 62 112 28 108 Z') +
        line('M 40 80.4 L 39 109 M 52 79.2 L 51.5 110 M 64 79 L 64 110 M 76 79.4 L 76.5 110 M 86 80.4 L 86.5 109', 0.8),
      back: () => HAT_DRAW.beanie.front(),
    },
  };
  const POM = (cx) => `<circle cx="${cx}" cy="26" r="9" fill="${FABRIC}" stroke="${INK}" stroke-width="1.6"/>` +
    line(`M ${cx - 5} 22 Q ${cx} 26 ${cx + 5} 22 M ${cx - 6} 27 Q ${cx} 31 ${cx + 6} 27`, 0.8);

  function hatSvg(location, kind, handle) {
    const spot = getSpot(location);
    const view = (spot && spot.view) || 'front';
    const h = String(handle || '').toLowerCase();
    const draw = HAT_DRAW[kind] || HAT_DRAW.cap;
    let drawing = view === 'side' ? draw.side() : view === 'back' ? draw.back() : draw.front();
    if (kind === 'beanie' && /pom/.test(h)) drawing += POM(view === 'side' ? 62 : 60);
    if (view === 'side' && location === 'Hat Right Side') drawing = `<g transform="translate(120 0) scale(-1 1)">${drawing}</g>`;
    const rect = rectFor(location, kind);
    let area = '';
    if (rect) {
      area = `<rect x="${rect.x}" y="${rect.y}" width="${rect.width}" height="${rect.height}" rx="1.6" fill="${ACCENT_FILL}" fill-opacity="0.42" stroke="${ACCENT}" stroke-width="1" stroke-dasharray="2 1.2"/>`;
    }
    const markup = `<svg width="160" height="200" viewBox="0 0 120 150" xmlns="http://www.w3.org/2000/svg">${drawing}${area}</svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(markup)));
  }

  // garment: 'tee' | 'hoodie' | 'crew' | 'cap' | 'bucket' | 'beanie' (see garmentOf); defaults to the product page's garment
  function svg(location, view, garment, handle) {
    const spot = getSpot(location);
    const isBack = String(view || (spot && spot.view) || '').toLowerCase() === 'back';
    const h = handle || pageHandle();
    const g = garment || garmentOf(h);
    if (HAT_RECTS[g]) return hatSvg(location, g, h);

    const bottoms = ['pants', 'shorts', 'leggings'];
    const drawing = drawGarment(LEG_SPOTS.includes(location) && !bottoms.includes(g) ? 'pants' : g, isBack, hasZip(h));

    const pocket = location === 'Over Pocket'
      ? `<path d="M 44 92 L 76 92 L 82 116 L 38 116 Z" fill="none" stroke="${INK}" stroke-width="0.9" stroke-dasharray="2 1.4" opacity="0.7"/>`
      : '';

    let area = '';
    const drawn = drawnBox(h, location, g);
    const rect = drawn ? drawn.rect : rectFor(location, g);
    if (drawn) {
      const a = drawn.area;
      area += `<rect x="${a.x.toFixed(2)}" y="${a.y.toFixed(2)}" width="${a.width.toFixed(2)}" height="${a.height.toFixed(2)}" rx="1" fill="none" stroke="${INK}" stroke-width="0.6" stroke-dasharray="1.4 1.4" opacity="0.4"/>`;
    }
    if (rect) {
      const cx = rect.x + rect.width / 2;
      const cy = rect.y + rect.height / 2;
      const box = `<rect x="${rect.x}" y="${rect.y}" width="${rect.width}" height="${rect.height}" rx="1.6" fill="${ACCENT_FILL}" fill-opacity="0.42" stroke="${ACCENT}" stroke-width="1" stroke-dasharray="2.2 1.3"/>`;
      area += rect.rot ? `<g transform="rotate(${rect.rot} ${cx} ${cy})">${box}</g>` : box;
    }

    const markup = `<svg width="160" height="200" viewBox="0 0 120 150" xmlns="http://www.w3.org/2000/svg">${drawing}${pocket}${area}</svg>`;
    return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(markup)));
  }

  // Spots that physically overlap, so they can't both be printed. Shared by the
  // product-page picker (ea-builder-steps.js) and the cart pop-up (ea-cart-customisations.js).
  const CONFLICTS = {
    'Full Front': ['Front Left Chest', 'Front Right Chest', 'Center Front', 'Over Pocket', 'Lower Front', 'Lower Left', 'Lower Right'],
    'Center Back': ['Top Back', 'Lower Back'],
  };
  // With a handle, spots whose boxes collide inside THIS blank's safe area conflict too
  // (on a 15 cm tall cropped tank a chest logo and a lower-front print overlap).
  const OVERLAP_SLACK_CM = 0.5;
  function boxesOverlap(handle, a, b) {
    const sa = getSpot(a);
    const sb = getSpot(b);
    if (!sa || !sb || sa.view !== sb.view || /Sleeve$|Side$|Leg$/.test(a + ' ' + b)) return false;
    const A = printBox(handle, a);
    const B = printBox(handle, b);
    if (!A || !B || A.sleeve || B.sleeve) return false;
    const ox = Math.min(A.x + A.w, B.x + B.w) - Math.max(A.x, B.x);
    const oy = Math.min(A.y + A.h, B.y + B.h) - Math.max(A.y, B.y);
    return ox > OVERLAP_SLACK_CM && oy > OVERLAP_SLACK_CM;
  }
  const conflictsWith = (a, b, handle) =>
    a !== b && ((CONFLICTS[a] || []).includes(b) || (CONFLICTS[b] || []).includes(a) || (!!handle && boxesOverlap(handle, a, b)));

  // Cart lines can carry a spot's value ("Center Back"), its label ("Left Chest")
  // or a British spelling ("Centre Back"); map any of them to the spot value.
  function normaliseSpot(text) {
    const raw = String(text || '').trim();
    if (spotIndex[raw]) return raw;
    if (HAT_ALIASES[raw]) return HAT_ALIASES[raw];
    const key = (v) => v.toLowerCase().replace(/[-_]+/g, ' ').replace(/centre/g, 'center');
    const k = key(raw);
    const all = Object.values(spotIndex);
    const hit = all.find((sp) => key(sp.value) === k) || all.find((sp) => key(sp.label) === k);
    return hit ? hit.value : raw;
  }

  // Garment thumbnail with the customer's artwork laid over the print area (cart lines,
  // product-page tiles). The garment is the SVG above; the artwork is a separate <img>
  // positioned in % of the 120×150 viewBox, so it lines up at any size as long as the
  // thumbnail keeps the SVG's 4:5 shape. An SVG used as <img src="data:…"> can't load
  // outside images, which is why the artwork isn't drawn inside the SVG itself.
  const escAttr = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const pct = (v, total) => +((v / total) * 100).toFixed(3);
  const IMAGE_URL_RE = /\.(png|jpe?g|webp|gif|svg)(\?|#|$)/i;

  // Only files a browser can show (PDF/AI/EPS artwork keeps the plain print area)
  const isImageUrl = (url) => /^(blob:|data:image\/)/i.test(String(url || '')) || IMAGE_URL_RE.test(String(url || ''));

  function artStyle(location, garment, handle) {
    const drawn = drawnBox(handle, location, garment);
    const r = drawn ? drawn.rect : rectFor(location, garment);
    if (!r) return '';
    return `left:${pct(r.x, 120)}%;top:${pct(r.y, 150)}%;width:${pct(r.width, 120)}%;height:${pct(r.height, 150)}%${r.rot ? `;transform:rotate(${r.rot}deg)` : ''}`;
  }

  function artImg(location, artUrl, garment, handle) {
    const style = artUrl && isImageUrl(artUrl) ? artStyle(location, garment, handle) : '';
    return style ? `<img class="ea-thumb__art" src="${escAttr(artUrl)}" alt="" style="${style}">` : '';
  }

  // handle picks the drawing (tee / hoodie / crew); defaults to the product page's product
  function thumbHTML(location, view, artUrl, className, handle) {
    const spot = getSpot(location);
    const h = handle || pageHandle();
    const g = garmentOf(h);
    const art = artImg(location, artUrl, g, h);
    return `<span class="ea-thumb${className ? ' ' + className : ''}${art ? ' has-art' : ''}" data-spot="${escAttr(location)}" data-garment="${g}" data-handle="${escAttr(h)}">` +
      `<img class="ea-thumb__garment" src="${svg(location, view || (spot && spot.view), g, h)}" alt="" width="160" height="200">` +
      `${art}</span>`;
  }

  // Swap the artwork on a thumbnail made by thumbHTML (null clears it)
  function setThumbArt(thumb, artUrl) {
    if (!thumb) return;
    const old = thumb.querySelector('.ea-thumb__art');
    if (old) old.remove();
    const img = artImg(thumb.dataset.spot, artUrl, thumb.dataset.garment, thumb.dataset.handle);
    if (img) thumb.insertAdjacentHTML('beforeend', img);
    thumb.classList.toggle('has-art', !!img);
  }

  window.EAPrintPlacements = { hatKindOf, HAT_SPOT_VALUES, AREAS, areasFor, allowedAreaIds, allowedSpotValues, kindOf, getSize, smallZoneBox, getSpot, svg, garmentOf, thumbHTML, setThumbArt, isImageUrl, printBox, fitsSafeArea, CONFLICTS, conflictsWith, normaliseSpot };
})();

